// React & Vendor Libs
const { useState, useCallback, useMemo } = wp.element;

// NekoUI
import { NekoButton, NekoPage, NekoHeader, NekoProgress, NekoTheme, NekoContainer, NekoSwitch, NekoTable, NekoPaging,
  NekoTabs, NekoTab, NekoWrapper, NekoColumn, NekoModal, NekoToolbar, NekoIcon, NekoTypo, NekoSelect, NekoOption,
  NekoCheckbox } from '@neko-ui';
import { useNekoTasks } from '@neko-ui';
import ReactJson from 'react-json-view'

import { prefix, domain, isRegistered, isPro } from '@app/settings';
import { LicenseBlock } from '@common';

import useDatabaseCleanerContext, { totalDbSizeKey, modeOptions, viewableSQLColumns } from '../context';
import TablesTab from './Settings.Tables';
import CoreTab from './Settings.Core';
import PostTypesTab from './Settings.PostTypes';
import OptionsTab from './Settings.Options';
import SettingsTab from './Settings.Settings';
import CronJobsTab from './Settings.CronJobs';
import QueriesTab from './Settings.Queries';

import { takeNap, formatMBytes, executeForAtLeast } from './../helper';
import ChartSizes from './ChartSizes';
import { LoadEntryModal } from './styled/NekoModal.styled';
import TableEntryModal from './TableEntryModal';

const Settings = () => {

  const { apiErrors, modals, totalSize, previousTotalSize, busyAction, loadEntryItemPage, loadEntryItemCount,
    loadEntryItemLimit, entriesTableColumns, loadEntryPrevItem, loadEntryNextItem, hideMessage, entries, plugins,
    selectedUsedBy, mode, delay, tableEntries, filteredTableColumns, tableColumnsFilter, tableCount, tableLimit, tablePage } = useDatabaseCleanerContext();
  const { toggleModal, updateClickedTabByIndex, deleteEntries, pushBusyCleanBulkTasks, updateTableColumnsFilter,
    getAutoCleanItems, resetAutoCleanItemsCount, popBusyCleanBulkTasks, getOptionValue, setLoadEntryItemPage,
    getTotalDbSizes, setSelectedUsedBy, setLoadEntryItem, updateOption, setTablePage, setTable } = useDatabaseCleanerContext();

  const [ status, setStatus ] = useState();
  const [ step, setStep ] = useState(0);
  const [ totalStep, setTotalStep ] = useState(0);
  const [ finalMessage, setFinalMessage ] = useState('');
  const [ busy, setBusy ] = useState(false);
  const bulkTasks = useNekoTasks();
  const percentLabel = useMemo(() => {
    if (!totalSize?.size || !previousTotalSize?.size) { return null; }
    let percent = (totalSize.size > previousTotalSize.size ? previousTotalSize.size / totalSize.size : totalSize.size / previousTotalSize.size) * 10000;
    percent = Math.round(10000 - percent) / 100;
    return totalSize.size > previousTotalSize.size ? percent : percent * -1;
  }, [totalSize?.size, previousTotalSize?.size]);
  const isModeExpert = mode === modeOptions.on.value;

  const onActionFinished = (items, errorCount = null) => {
    errorCount = errorCount !== null ? errorCount : bulkTasks.getErrorCount();
    if (errorCount > 0) {
      setFinalMessage(`The process has finished, but ${errorCount} error(s) occurred.`);
    }
    bulkTasks.reset();
    setStatus();
    setStep(0);
    setTotalStep(0);
    resetAutoCleanItemsCount(items);
  }

  const onCleanNow = async () => {
    let errorsCount = 0;
    pushBusyCleanBulkTasks();
    setBusy(true);

    // STEP 1: Selected items.
    setStatus(`Getting tasks...`);

    // Get the items to clean.
    const items = await getAutoCleanItems();

    console.log("Tasks for Database Cleaner:", items);
    const totalTimes = items.map(x => x.times).reduce((a, b) => a + b);
    if (!totalTimes) {
      alert("Everything is clean already! :)");
      onActionFinished(items, errorsCount);
      popBusyCleanBulkTasks();
      setBusy(false);
      return;
    }

    console.log(entries);

    // And get the custom queries.
    // const filteredCustomQueries = customQueries
    //   .filter(v => !!v.query_delete)
    //   .filter(v => v.clean_style === 'auto')
    //   .map(v => {
    //     return {
    //       item: v.item,
    //       name: v.name
    //     }
    //   });

    // Log the db size before starting to clean.
    //await logDbSize();

    // Preparing the tasks.
    const isAutoClean = true;
    const itemsTasks = [];
    await executeForAtLeast(500, async () => {
      for (let i = 0; i < items.length; i+=1) {
        const data = items[i];
        const times = data.times;
        for (let j = 0; j < times; j+=1) {
          const task = async (signal) => {
            setStatus(<small>{data.name}</small>);
            setStep(step => step + 1);
            await takeNap(delay);
            const result = await deleteEntries({ item: data.item, signal, is_auto_clean: isAutoClean });
            if (result.data.finished && j + 1 < times) {
              bulkTasks.setAlwaysSkip();
            }
            return result;
          }
          itemsTasks.push(task);
        }
      }
      setTotalStep(itemsTasks.length);
    });

    // STEP 2: Clean all the seleted items.
    setStatus(`Cleaning items...`);

    await bulkTasks.start(itemsTasks);
    bulkTasks.reset();
    errorsCount += bulkTasks.getErrorCount();

    // // STEP 3: Clean all the custom query's items.
    // setStatus(`Cleaning custom queries...`);
    // const customQueriesTasks = filteredCustomQueries.map(v => async (signal) => {
    //   setStatus(`${getSectionNameFromItem(v.item)}: ${v.name}`);
    //   setStep(step => step + 1);
    //   await takeNap(delay);
    //   return await postFetch(`${apiUrl}/custom_query_delete`, { json: { item: v.item, is_auto_clean: isAutoClean }, signal, nonce: restNonce });
    // });
    // await bulkTasks.start(customQueriesTasks);
    // bulkTasks.reset();
    // errorsCount += bulkTasks.getErrorCount();

    // Log the db size after cleaning.
    //await logDbSize();

    onActionFinished(items, errorsCount);
    popBusyCleanBulkTasks();
    setBusy(false);
  };

  const onTabChanged = (_tabIndex, tabAttr, _ev) => {
    updateClickedTabByIndex(tabAttr.title);
  }

  const closeLoadEntryModal = useCallback(() => {
    toggleModal({modal: 'loadEntry', enable: false});
    setLoadEntryItem({ loadEntryItem: null });
  }, []);

  const closeOptionValueModal = useCallback(() => {
    toggleModal({modal: 'optionValue', enable: false});
    setLoadEntryItem({ loadEntryItem: null });
  }, []);

  const closeTableModal = useCallback(() => {
    toggleModal({modal: 'table', enable: false});
    setTimeout(() => setTable({ table: [] }), 500);
  }, []);

  const goNextOrPrevOption = useCallback((loadEntryItem) => {
    getOptionValue({ optionName: loadEntryItem });
    setLoadEntryItem({ loadEntryItem });
  }, []);

  const updateModeOption = useCallback(async (value) => {
    updateOption({ param: { name: 'mode', value } });
  }, [updateOption]);

  return (
    <NekoPage nekoErrors={[ apiErrors ]}>

      <NekoHeader title='Database Cleaner | Settings' subtitle='By Jordy Meow'>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <NekoButton className="header"
            onClick={() => window.open('https://meowapps.com/database-cleaner/tutorial/', "_blank")}>
            Tutorial
          </NekoButton>
        </div>
      </NekoHeader>

      <NekoWrapper>

        <NekoColumn full>

        {!hideMessage &&
          <NekoContainer>
            <NekoTypo p>
              <p>While using Database Cleaner is easy, I strongly recommend you to read the <a href='https://meowapps.com/database-cleaner/tutorial/' target='_blank'>tutorial</a> carefully. This plugin has more capabilities than other plugins of the same kind, so you might find new and interesting ways to handle your cleanups. If any issue, or feature request, please let me know on the <a href='https://wordpress.org/support/plugin/database-cleaner/' target='_blank'>WordPress Forums</a>. <b>Last but not least, backup your DB before doing anything!</b> If you don't know how, give a try to <a href='http://meow.click/blogvault' target='_blank'>BlogVault</a>. </p>
            </NekoTypo>
          </NekoContainer>
        }

          <div style={{ marginBottom: '30px' }}>
            <NekoToolbar style={{ alignItems: 'center' }}>
              <NekoButton icon='play' disabled={busy} onClick={onCleanNow}>Auto Clean</NekoButton>
              <NekoSwitch onLabel={modeOptions.on.label} offLabel={modeOptions.off.label}
                onValue={modeOptions.on.value} offValue={modeOptions.off.value}
                onBackgroundColor={NekoTheme.purple} offBackgroundColor={NekoTheme.green}
                width="85px" height="30px" style={{ marginLeft: 5 }}
                onChange={updateModeOption} checked={isModeExpert}
              />

              {status &&
                <div style={{ display: 'flex', fontFamily: 'Lato', padding: '0 10px', flexDirection: 'column', fontWeight: 600,
                  width: 180, flexDirection: 'column', height: 30, lineHeight: '16px', justifyContent: 'center', 
                  textTransform: 'uppercase' }}>
                  {totalStep > 0 && <div style={{ color: '#144675', fontSize: 17 }}>{`STEP ${step}/${totalStep}`}</div>}
                  <div style={{ color: '#007bba' }}>{status}</div>
                </div>
              }

              <div style={{ flex: 1 }}>
                {status && <NekoProgress status={x => bulkTasks.max ? `${x}%` :
                  <NekoIcon icon="replay" height={16} spinning={true}></NekoIcon>} busy={bulkTasks.busy} 
                  value={bulkTasks.value} max={bulkTasks.max} onStopClick={bulkTasks.stop} />}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', margin: '-14px 0 -12px 0' }}>

                <ChartSizes />

                <div style={{ textAlign: 'right' }}>
                  <NekoTypo p style={{ fontSize: 10, margin: 0, color: NekoTheme.darkGray, lineHeight: 1 }}>
                      TODAY
                      <NekoTypo style={{ fontSize: 10, color: percentLabel > 0 ? NekoTheme.red : NekoTheme.green, marginLeft: '2px' }}>
                        ({percentLabel > 0 ? '+' : percentLabel < 0 ? '-' : ''}{Math.abs(percentLabel)}%)
                      </NekoTypo>
                    </NekoTypo>
                  <NekoTypo p style={{ fontSize: 22, fontWeight: 'bold', margin: 0, lineHeight: 1 }}>
                    {formatMBytes(totalSize?.size)}
                  </NekoTypo>
                </div>
                
                <NekoIcon icon="replay" width={20} height={20} containerStyle={{ alignSelf: 'end', padding: '0 5px 5px 8px' }} color={NekoTheme.blue} spinning={busyAction.some(v => v.item === totalDbSizeKey)} onClick={() => getTotalDbSizes()} />
                <NekoIcon icon="database" width={36} height={36} style={{ marginLeft: 0 }} />
              </div>
            </NekoToolbar>
          </div>

          <NekoTabs keepTabOnReload={true} onChange={onTabChanged} callOnTabChangeFirst>

            <NekoTab title='WordPress Core'>
              <CoreTab />
            </NekoTab>

            <NekoTab title='Post Types' hidden={!isModeExpert}>
              <PostTypesTab />
            </NekoTab>

            <NekoTab title='Tables'>
              <TablesTab />
            </NekoTab>

            <NekoTab title='Options' hidden={!isModeExpert}>
              <OptionsTab />
            </NekoTab>

            <NekoTab title='Cron Jobs' hidden={!isModeExpert}>
              <CronJobsTab />
            </NekoTab>

            <NekoTab title='Custom Queries' hidden={!isModeExpert}>
              <QueriesTab />
            </NekoTab>

            <NekoTab title='Settings & Logs'>
              <SettingsTab />
            </NekoTab>

            <NekoTab title='License'>
              <LicenseBlock domain={domain} prefix={prefix} isPro={isPro} isRegistered={isRegistered} />
            </NekoTab>

          </NekoTabs>

        </NekoColumn>

      </NekoWrapper>

      <NekoModal
        isOpen={!!modals.deleteAll.enable}
        onRequestClose={() => toggleModal({modal: 'deleteAll', enable: false})}
        title={modals.deleteAll.title}
        content="Are you sure?"
        onOkClick={() => {
          modals.deleteAll.onNext();
          toggleModal({modal: 'deleteAll', enable: false});
        }}
        onCancelClick={() => toggleModal({modal: 'deleteAll', enable: false})}
        okOnEnter={true}
      />

      <NekoModal isOpen={finalMessage} title="Finished" ok='Close' content={finalMessage} 
        onOkClick={() => { setFinalMessage(''); bulkTasks.reset(); }}
      />

      <LoadEntryModal
        isOpen={!!modals.loadEntry.enable}
        onRequestClose={closeLoadEntryModal}
        title={modals.loadEntry.title}
        contentWidth='100%'
        content={<>
          {/* <p style={{ marginBottom: 15 }}>You will find those results with all their fields in the console.</p> */}
          <div style={{ maxHeight: '65vh', overflowY: 'auto' }}>
            <div style={{ marginRight: 10 }}>
            {entries.length > 0 ? <NekoTable columns={entriesTableColumns} selectedItems={[]}
              data={entries?.map && entries.map(v => {
                const list = Object.keys(v).filter(o => viewableSQLColumns.includes(o)).map(o => {
                  let content = v[o];
                  // if content is longer than 100 chars, show only first 100 chars
                  if (typeof content === 'string' && content.length > 100) {
                    content = <>{content.substr(0, 100) + '...'} <br /><small><a href="#" onClick={() => { console.log("Content", v[o]); }}>
                      Write to Browser Console
                    </a></small></>
                  }
                  return {
                    [o]: <p>{content}</p>
                  }
                })
                return list.reduce((obj, item) => Object.assign(obj, item), {});
              })} />: <p>No entries.</p>}
            </div>
          </div>
        </>}
        onOkClick={closeLoadEntryModal}
        ok='Close'
        okOnEnter={true}
        customButtons={
          <div style={{ marginRight: 'auto' }}>
            <NekoPaging currentPage={loadEntryItemPage} limit={loadEntryItemLimit}
              total={loadEntryItemCount} onClick={(page) => setLoadEntryItemPage(page)} />
          </div>
        }
        customButtonsPosition="left"
      />

      <TableEntryModal />

      <LoadEntryModal
        isOpen={!!modals.optionValue.enable}
        onRequestClose={closeOptionValueModal}
        title={modals.optionValue.title}
        contentWidth='100%'
        content={<>
          <div style={{ maxHeight: '65vh', overflowY: 'auto', padding: 10 }}>
            <ReactJson src={typeof entries === 'object' ? entries : { text: entries }}
              name='data' iconStyle='circle' collapseStringsAfterLength={512} 
              enableClipboard={false} displayDataTypes={false}
            />
          </div>
        </>}
        onOkClick={closeOptionValueModal}
        okOnEnter={true}
        customButtonsPosition="left"
      />

      <NekoModal
        isOpen={!!modals.usedBy.enable}
        onRequestClose={() => { toggleModal({modal: 'usedBy', enable: false}); setSelectedUsedBy({ items: [], value: "unknown"}); }}
        title={modals.usedBy.title}
        // title={`Used By${selectedUsedBy?.items?.length > 1 ? ` (${selectedUsedBy?.items?.length} selected items)` : ''}`}
        content={<>
          <NekoSelect scrolldown={true} value={selectedUsedBy.value} textFiltering
              onChange={(value) => setSelectedUsedBy({ ...selectedUsedBy, value })}
              style={{ maxHeight: '200px', marginBottom: 20 }}>
            <NekoOption value="unknown" label="Unknown" />
            {plugins.map(v => {
              return <NekoOption value={v.slug} label={v.name} />
            })}
          </NekoSelect>
          {selectedUsedBy.value !== 'unknown' && <div style={{ marginTop: -15, marginBottom: 15 }}>
            <small>Selected Plugin Slug: {selectedUsedBy.value}</small>
          </div>}
          {!isPro && <NekoTypo>
            The <b>Pro Version</b> of Database Cleaner knows much more about how data is used, and can simplify this for you! Please check <a href="https://meowapps.com/products/database-cleaner-pro/" target="_blank">Database Cleaner Pro</a>. And don't hesitate to <a href="https://meowapps.com/contact" target="_blank">contact me</a> for a discount! 💕
          </NekoTypo>}
          {isPro && 
            <NekoTypo>
              Please visit the <b>Settings</b> tab to access all your <b>Used By</b> data. Don't hesitate to share it <a href="https://meowapps.com/support/" target="_blank">with me</a> so that I can make this plugin better.
            </NekoTypo>
          }
        </>}
        cancel="Unassign"
        onCancelClick={() => { 
          modals.usedBy.onNext({ items: selectedUsedBy.items, value: "unknown"});
          toggleModal({modal: 'usedBy', enable: false});
          setSelectedUsedBy({ items: [], value: "unknown"});
        }}
        ok="Assign"
        onOkClick={() => {
          modals.usedBy.onNext(selectedUsedBy);
          toggleModal({modal: 'usedBy', enable: false});
          setSelectedUsedBy({ items: [], value: "unknown"});
        }}
        okOnEnter={true}
      />

    </NekoPage>
  );
};

export default Settings;